import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/login">
          <Button variant="ghost" size="sm" className="gap-2 mb-8 rounded-sm">
            <ArrowLeft size={16} /> Back
          </Button>
        </Link>

        <h1 className="text-3xl font-semibold text-slate-900 dark:text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Privacy Policy
        </h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: March 11, 2026</p>

        <div className="prose prose-slate dark:prose-invert max-w-none text-sm leading-relaxed space-y-6" data-testid="privacy-policy-content">
          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">1. Introduction</h2>
            <p>ProcureAide ("we", "our", "us") operates the Contrack Contract Lifecycle Management platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data when you use our platform, in compliance with the General Data Protection Regulation (GDPR) (EU) 2016/679 and applicable data protection laws.</p>
            <p>By using Contrack, you acknowledge that you have read and understood this Privacy Policy. If you do not agree with our practices, please do not use the platform.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">2. Data Controller</h2>
            <p>ProcureAide Ltd is the data controller responsible for your personal data. For any data protection queries, contact our Data Protection Officer at: <strong>dpo@procureaide.com</strong></p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">3. Personal Data We Collect</h2>
            <p>We collect the following categories of personal data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Account Information:</strong> Name, email address, department, role, and password (stored as a salted bcrypt hash).</li>
              <li><strong>Contract Data:</strong> Contract content, metadata, parties, values, dates, deliverables, KPIs, and obligations you create or manage on the platform.</li>
              <li><strong>Usage Data:</strong> Login timestamps, actions performed (audit trail), IP addresses, and session information.</li>
              <li><strong>Communication Data:</strong> Email notifications and reminder preferences.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">4. Legal Basis for Processing</h2>
            <p>We process your personal data under the following legal bases (GDPR Article 6):</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Consent (Art. 6(1)(a)):</strong> You provide explicit consent during registration for data processing necessary to provide the service.</li>
              <li><strong>Contract Performance (Art. 6(1)(b)):</strong> Processing is necessary to deliver the contract management services you have subscribed to.</li>
              <li><strong>Legitimate Interest (Art. 6(1)(f)):</strong> Security monitoring, fraud prevention, and service improvement.</li>
              <li><strong>Legal Obligation (Art. 6(1)(c)):</strong> Compliance with applicable laws and regulations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">5. How We Use Your Data</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide, maintain, and improve the Contrack platform</li>
              <li>Authenticate users and manage access controls</li>
              <li>Process contract workflows, approvals, and notifications</li>
              <li>Generate analytics and reports on contract portfolios</li>
              <li>AI-assisted analysis of contract content (obligations, risks, KPIs)</li>
              <li>Send email notifications and compliance reminders</li>
              <li>Monitor security and prevent unauthorized access</li>
              <li>Maintain audit trails for compliance and accountability</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">6. Data Sharing and Third Parties</h2>
            <p>We may share your data with:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>AI Service Providers:</strong> Contract content is processed by OpenAI (GPT-4o) for obligation extraction, risk review, and deliverable analysis. Data is transmitted securely and not used for model training.</li>
              <li><strong>Email Service Providers:</strong> Email addresses and notification content are processed by Resend for transactional emails.</li>
              <li><strong>Infrastructure Providers:</strong> Data is stored on secure cloud infrastructure with encryption at rest and in transit.</li>
            </ul>
            <p>We do not sell your personal data to any third party.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">7. Your Rights Under GDPR</h2>
            <p>You have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Right of Access (Art. 15):</strong> Request a copy of all personal data we hold about you. Available via Settings &gt; Security &gt; "Download My Data".</li>
              <li><strong>Right to Rectification (Art. 16):</strong> Update inaccurate personal data via your profile settings.</li>
              <li><strong>Right to Erasure (Art. 17):</strong> Request deletion of your account and anonymization of personal data. Available via Settings &gt; Security &gt; "Delete My Account".</li>
              <li><strong>Right to Restrict Processing (Art. 18):</strong> Request restriction of processing in certain circumstances.</li>
              <li><strong>Right to Data Portability (Art. 20):</strong> Receive your data in a structured, machine-readable format (JSON export).</li>
              <li><strong>Right to Object (Art. 21):</strong> Object to processing based on legitimate interests.</li>
              <li><strong>Right to Withdraw Consent (Art. 7(3)):</strong> Withdraw consent at any time without affecting the lawfulness of prior processing.</li>
            </ul>
            <p>To exercise any of these rights, contact us at <strong>dpo@procureaide.com</strong> or use the self-service tools in your account settings.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">8. Data Retention</h2>
            <p>We retain your personal data for as long as your account is active or as needed to provide services. After account deletion, personal data is anonymized within 30 days. Audit logs are retained for 7 years for compliance purposes. Contract data may be retained as required by applicable laws and contractual obligations.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">9. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Passwords hashed with bcrypt (salted)</li>
              <li>JWT-based authentication with token expiration</li>
              <li>Account lockout after 5 failed login attempts</li>
              <li>HTTPS/TLS encryption for all data in transit</li>
              <li>Security headers (HSTS, X-Frame-Options, CSP)</li>
              <li>Role-based access control (RBAC) with configurable permissions</li>
              <li>Comprehensive audit logging of all sensitive actions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">10. Cookies</h2>
            <p>Contrack uses only essential cookies required for authentication and session management. We do not use tracking or advertising cookies. You can manage cookie preferences via the cookie consent banner.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">11. International Data Transfers</h2>
            <p>Your data may be processed in countries outside the European Economic Area (EEA). Where this occurs, we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) approved by the European Commission.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">12. Data Breach Notification</h2>
            <p>In the event of a personal data breach that poses a risk to your rights and freedoms, we will notify the relevant supervisory authority within 72 hours of becoming aware of the breach (GDPR Article 33). Where the breach is likely to result in a high risk, we will also notify affected individuals without undue delay (GDPR Article 34).</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">13. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of material changes by email or through a prominent notice on the platform. Your continued use after changes constitutes acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mt-8 mb-3">14. Contact Us</h2>
            <p>For any questions about this Privacy Policy or our data practices:</p>
            <ul className="list-none space-y-1">
              <li>Data Protection Officer: <strong>dpo@procureaide.com</strong></li>
              <li>ProcureAide Ltd, London, United Kingdom</li>
            </ul>
            <p className="mt-4">You also have the right to lodge a complaint with a supervisory authority, in particular in the EU Member State of your habitual residence, place of work, or place of the alleged infringement.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
